/**
 * PATH: packages/job-hub/src/jobs/qa/useItpCover.ts
 * FILE_ID: JOBHUB-QA-ITP-COVER-HOOK-001
 * PURPOSE: Cover fields for Print and Save PDF from the job, brand, team, and participants.
 * LANDMINE: The job query key stays ['job', id]. A second job fetch would
 *      race the hub cache. Team uses its own ['job-team', id] key.
 */
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@quotech/auth'
import { useQuoteBrand } from '@quotech/quote-builder/runtime/quoteBrand'
import { useJobHubDataAdapter } from '../jobHubDataAdapter'
import type { Job } from '../jobDetailTypes'
import { mergeCoverParticipants, type ItpCoverFields, type ItpCoverParticipant } from './itpCover'

function rowsOf(data: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(data)) return data as Array<Record<string, unknown>>
  const results = (data as { results?: unknown[] } | null)?.results
  return Array.isArray(results) ? results as Array<Record<string, unknown>> : []
}

function teamRows(data: unknown): Array<Record<string, unknown>> {
  const team = (data as { team?: unknown[] } | null)?.team
  return Array.isArray(team) ? team as Array<Record<string, unknown>> : []
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function person(row: Record<string, unknown>, roleKey: string, phoneKey: string): ItpCoverParticipant {
  const rawId = row.user_id ?? row.user
  const userId = typeof rawId === 'number' ? rawId : undefined
  return {
    name: text(row.user_name) || text(row.name),
    email: text(row.user_email_display) || text(row.email),
    role: text(row[roleKey]),
    phone: text(row[phoneKey]),
    userId,
  }
}

function exclusionIds(data: unknown): number[] {
  if (!Array.isArray(data)) return []
  return data.filter((id): id is number => typeof id === 'number')
}

export function useItpCover(jobId: number, extra?: Partial<ItpCoverFields>): ItpCoverFields {
  const { client } = useJobHubDataAdapter()
  const brand = useQuoteBrand()
  const { user } = useAuth()
  const { data: job } = useQuery<Job>({
    queryKey: ['job', String(jobId)],
    queryFn: () => client.get(`/jobs/${jobId}`).then((response) => response.data),
  })
  const { data: participants } = useQuery({
    queryKey: ['qa-participants', String(jobId)],
    queryFn: () => client.get('/api/qa/participants/', { params: { job_id: jobId } }).then((response) => response.data),
  })
  const { data: team } = useQuery({
    queryKey: ['job-team', String(jobId)],
    queryFn: () => client.get(`/api/jobs/${jobId}/team/`).then((response) => response.data),
  })
  const { data: exclusions } = useQuery({
    queryKey: ['qa-team-exclusions', jobId],
    queryFn: () => client.get('/api/qa/team-exclusions/', { params: { job_id: jobId } }).then((response) => response.data),
  })
  const authName = [text(user?.first_name), text(user?.last_name)].filter(Boolean).join(' ')
    || text(user?.full_name) || text(user?.name) || text(user?.email)
  const { data: me } = useQuery({
    queryKey: ['auth-user'],
    enabled: !authName,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: () => client.get('/api/auth/user/').then((response) => response.data),
  })
  const meRow = ((me as { user?: unknown } | undefined)?.user ?? me ?? {}) as Record<string, unknown>
  const preparedBy = authName
    || [text(meRow.first_name), text(meRow.last_name)].filter(Boolean).join(' ')
    || text(meRow.full_name) || text(meRow.name) || text(meRow.email)
  const qaPeople = rowsOf(participants).map((row) => person(row, 'role_on_job', 'user_phone'))
  const teamPeople = teamRows(team).map((row) => person(row, 'role', 'phone'))
  return {
    companyName: brand.companyName,
    address: brand.address,
    phone: brand.phone,
    abn: brand.abn,
    logoSrc: (brand.logoDataUri || brand.logoUrl || '').trim(),
    logoHeightPx: brand.logoDisplayHeightPx,
    clientName: job?.client_name ?? '',
    clientContactName: job?.client_contact_name ?? '',
    clientAddress: job?.client_address ?? '',
    clientPhone: job?.client_phone ?? '',
    clientEmail: job?.client_email ?? '',
    siteAddress: job?.site_address ?? '',
    coverImageUrl: job?.itp_cover_image_url ?? null,
    preparedBy,
    generatedAt: new Date().toISOString(),
    participants: mergeCoverParticipants(qaPeople, teamPeople, new Set(exclusionIds(exclusions))),
    ...extra,
  }
}
